import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Image as ImageIcon } from 'lucide-react';
export interface FrequencySettingsValues {
  loyaltyTier?: string;
  adHocDiscount?: string;
  pausedDiscount?: string;
}
interface FrequencySettingsFieldsProps extends FrequencySettingsValues {
  cssHandle: string;
  storefrontHeading: string;
  customTiers: string[];
  onAddCustomTier: (tier: string) => void;
  onChange: (updates: FrequencySettingsValues) => void;
}
const PRESET_TIERS = ['None', 'Bronze', 'Silver', 'Gold', 'Platinum'];

// Settings shared by a frequency and a pack's release schedule:
// commitment, cutoff, loyalty tier, member discounts and storefront card content
export function FrequencySettingsFields({
  cssHandle,
  storefrontHeading,
  loyaltyTier,
  adHocDiscount,
  pausedDiscount,
  customTiers,
  onAddCustomTier,
  onChange
}: FrequencySettingsFieldsProps) {
  const [isCustomPending, setIsCustomPending] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const customInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isCustomPending) customInputRef.current?.focus();
  }, [isCustomPending]);
  const cancelCustomTier = () => {
    setIsCustomPending(false);
    setCustomValue('');
  };
  const confirmCustomTier = () => {
    const trimmed = customValue.trim();
    if (!trimmed) return;
    if (!customTiers.includes(trimmed)) onAddCustomTier(trimmed);
    onChange({
      loyaltyTier: trimmed
    });
    cancelCustomTier();
  };
  const tierSelectValue = PRESET_TIERS.includes(loyaltyTier || '') ? loyaltyTier || 'None' : customTiers.includes(loyaltyTier || '') ? loyaltyTier : isCustomPending ? '__custom__' : loyaltyTier || 'None';
  return <div className="space-y-4" data-id="element-4002" data-mp="332ejt0b67q4b">
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4003" data-mp="nctlgdbdkz1u">
        <div data-id="element-4004" data-mp="ie0gj9a5mgda">
          <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-4005" data-mp="iejopya05qe4">
            Min Commitment
          </label>
          <input type="number" defaultValue="3" className="w-full h-[36px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[13px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-4006" data-mp="pg5fp6a593su" />
          <p className="text-[11px] text-[#94A3B8] mt-1" data-id="element-4007" data-mp="14e7ihe9cpl91">
            Cycles before cancel allowed
          </p>
        </div>
        <div data-id="element-4008" data-mp="6qz0vuch0wcb">
          <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-4009" data-mp="6riamnrywef9">
            Edit Cutoff
          </label>
          <input type="number" defaultValue="5" className="w-full h-[36px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[13px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-4010" data-mp="bq202y5rs8cd" />
          <p className="text-[11px] text-[#94A3B8] mt-1" data-id="element-4011" data-mp="3k5dc5t3abzaf">Days before billing</p>
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-4012" data-mp="3p46pnliknqal">
        <div data-id="element-4013" data-mp="269u0suq0xc5a">
          <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-4014" data-mp="268ajr1wjmx2g">
            Loyalty Tier
          </label>
          <div className="relative" data-id="element-4015" data-mp="2288kt0i70hqv">
            <select value={tierSelectValue} onChange={e => {
            if (e.target.value === '__custom__') {
              setIsCustomPending(true);
              setCustomValue('');
            } else {
              setIsCustomPending(false);
              onChange({
                loyaltyTier: e.target.value
              });
            }
          }} className="w-full h-[36px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[13px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-4016" data-mp="1x8nl2nv785d3">
              {PRESET_TIERS.map(tier => <option key={tier} value={tier} data-id="element-4017" data-mp="14khwfq10xw7z">
                  {tier}
                </option>)}
              {customTiers.length > 0 && <option disabled data-id="element-4018" data-mp="zlww7qlrvcjj">──────────</option>}
              {customTiers.map(tier => <option key={tier} value={tier} data-id="element-4019" data-mp="2zmlcfqu7w05o">
                  {tier}
                </option>)}
              <option value="__custom__" data-id="element-4020" data-mp="34mr5nbg4xupv">Custom...</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#64748B] pointer-events-none" data-id="element-4021" data-mp="2pxbh8242u1dc" />
          </div>
          {isCustomPending && <div className="mt-2 flex items-center gap-2" data-id="element-4022" data-mp="2uutrbvttymmx">
              <input ref={customInputRef} type="text" value={customValue} onChange={e => setCustomValue(e.target.value)} onKeyDown={e => {
            if (e.key === 'Enter') confirmCustomTier();
            if (e.key === 'Escape') cancelCustomTier();
          }} placeholder="Enter tier name..." className="flex-1 h-[32px] px-3 bg-white border border-[#0EA5E9] rounded-[6px] text-[13px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-4023" data-mp="2gw3umhaxaoz3" />
              <button onMouseDown={e => {
            e.preventDefault();
            confirmCustomTier();
          }} disabled={!customValue.trim()} aria-label="Add custom tier" className="w-[32px] h-[32px] flex-shrink-0 flex items-center justify-center rounded-[6px] bg-[#0EA5E9] text-white hover:bg-[#0284C7] disabled:opacity-40 disabled:cursor-not-allowed transition-colors z-10 relative" data-id="element-4024" data-mp="a6pc3muat9yy">
                <Check className="w-3.5 h-3.5" data-id="element-4025" data-mp="1zmsyq0pppfx8" />
              </button>
            </div>}
          <p className="text-[11px] text-[#94A3B8] mt-1" data-id="element-4026" data-mp="2depqg3ngxnt3">
            Tier assigned to subscribers on this plan
          </p>
        </div>
        <div data-id="element-4027" data-mp="29c2txpfurepk">
          <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-4028" data-mp="29ckugy9jsf3a">
            Ad-Hoc Discount
          </label>
          <div className="flex rounded-[6px] overflow-hidden border border-[#CBD5E1] focus-within:border-[#0EA5E9] focus-within:ring-[3px] focus-within:ring-[#0EA5E9]/20" data-id="element-4029" data-mp="2naja06zluhkd">
            <input type="number" value={adHocDiscount ?? '0'} onChange={e => onChange({
            adHocDiscount: e.target.value
          })} className="flex-1 h-[36px] px-3 bg-white text-[13px] text-[#0F172A] focus:outline-none min-w-0" data-id="element-4030" data-mp="sze6r3sfk75k" />
            <div className="bg-[#F8FAFC] px-3 flex items-center text-[13px] text-[#64748B] border-l border-[#CBD5E1] flex-shrink-0" data-id="element-4031" data-mp="2t0u9lpchvi5u">
              %
            </div>
          </div>
          <p className="text-[11px] text-[#94A3B8] mt-1" data-id="element-4032" data-mp="2x521fve91e8a">
            Member discount on non-subscription purchases
          </p>
        </div>
        <div data-id="element-4033" data-mp="371ojkcf6xr6m">
          <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-4034" data-mp="3703omloys688">
            Paused Discount
          </label>
          <div className="flex rounded-[6px] overflow-hidden border border-[#CBD5E1] focus-within:border-[#0EA5E9] focus-within:ring-[3px] focus-within:ring-[#0EA5E9]/20" data-id="element-4035" data-mp="3bxvhbhxfnwdh">
            <input type="number" value={pausedDiscount ?? '0'} onChange={e => onChange({
            pausedDiscount: e.target.value
          })} className="flex-1 h-[36px] px-3 bg-white text-[13px] text-[#0F172A] focus:outline-none min-w-0" data-id="element-4036" data-mp="3kr06bhhcr30i" />
            <div className="bg-[#F8FAFC] px-3 flex items-center text-[13px] text-[#64748B] border-l border-[#CBD5E1] flex-shrink-0" data-id="element-4037" data-mp="8p0eqq0zen1q">
              %
            </div>
          </div>
          <p className="text-[11px] text-[#94A3B8] mt-1" data-id="element-4038" data-mp="3s0a8jjs5scq">
            Discount applied when this subscription is paused
          </p>
        </div>
      </div>

      <div className="border-t-2 border-dashed border-[#E2E8F0] pt-4" data-id="element-4039" data-mp="3qvohi98k6y48">
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 space-y-5" data-id="element-4040" data-mp="rf8d6utqkpbl">
          <div className="flex justify-end" data-id="element-4041" data-mp="mgey4xyswq9b">
            <span className="text-[11px] font-semibold text-[#0EA5E9] uppercase tracking-[0.5px]" data-id="element-4042" data-mp="mhq1wxs8xy0f">
              {storefrontHeading}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4043" data-mp="dod3xhfbs67h">
            <div data-id="element-4044" data-mp="dm0kt0xtzpsu">
              <label className="block text-[12px] font-bold text-[#334155] mb-1.5" data-id="element-4045" data-mp="1a5zjfniffzlr">
                Card Tagline
              </label>
              <input type="text" placeholder="e.g. Skip, cancel or change at any time" className="w-full h-[40px] px-3 bg-white border border-[#E2E8F0] rounded-[6px] text-[13px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-4046" data-mp="15e81g1dc7278" />
              <p className="text-[12px] text-[#94A3B8] mt-1.5" data-id="element-4047" data-mp="1f66rh9asy2bb">
                Text shown below the frequency name on the card
              </p>
            </div>
            <div data-id="element-4048" data-mp="27u2ibxdox006">
              <label className="block text-[12px] font-bold text-[#334155] mb-1.5" data-id="element-4049" data-mp="27tja58dudpzc">
                CTA Text
              </label>
              <input type="text" placeholder="e.g. First box ships today!" className="w-full h-[40px] px-3 bg-white border border-[#E2E8F0] rounded-[6px] text-[13px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-4050" data-mp="2csdsyabt7ejg" />
              <p className="text-[12px] text-[#94A3B8] mt-1.5" data-id="element-4051" data-mp="mewqgqt0dr4g">
                Button or label text on the frequency card
              </p>
            </div>
          </div>
          <div data-id="element-4052" data-mp="35miwqcuq3bt4">
            <label className="block text-[12px] font-bold text-[#334155] mb-1.5" data-id="element-4053" data-mp="35m0sasxwpra4">
              Card Image
            </label>
            <div className="flex items-center gap-4" data-id="element-4054" data-mp="30qbq0hui5wa4">
              <div className="w-16 h-16 rounded-full border-2 border-dashed border-[#CBD5E1] flex items-center justify-center text-[#94A3B8] flex-shrink-0" data-id="element-4055" data-mp="3eoa3ll101ook">
                <ImageIcon className="w-6 h-6" data-id="element-4056" data-mp="3akkhr3bpll78" />
              </div>
              <div data-id="element-4057" data-mp="2mxca4eqt6021">
                <p className="text-[13px] text-[#475569]" data-id="element-4058" data-mp="2muq78sev48q5">
                  <span className="font-bold text-[#0F172A]" data-id="element-4059" data-mp="2hysnsd8aym95">Upload image</span>{' '}
                  or drag and drop
                </p>
                <p className="text-[12px] text-[#94A3B8] mt-0.5" data-id="element-4060" data-mp="2vr8w9tjz3chg">
                  PNG, JPG up to 2MB. Displayed at top of frequency card.
                </p>
              </div>
            </div>
          </div>
          <div data-id="element-4061" data-mp="236h4xpjlumcx">
            <label className="block text-[12px] font-bold text-[#334155] mb-1.5" data-id="element-4062" data-mp="1z7hx9x6hobhs">
              Card CSS
            </label>
            <textarea defaultValue={`/* Target this frequency card */\n.freq-card--${cssHandle} { }`} className="w-full h-[80px] px-4 py-3 bg-[#0F172A] border border-[#334155] rounded-[6px] text-[13px] text-[#E2E8F0] font-mono focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 resize-y" data-id="element-4063" data-mp="39eagm0ughwd4" />
            <p className="text-[12px] text-[#94A3B8] mt-1.5 text-right" data-id="element-4064" data-mp="2vf2d4vr9g234">
              CSS scoped to this frequency card only. Class:{' '}
              <code className="font-mono text-[#64748B]" data-id="element-4065" data-mp="2lpranryvpvo7">
                .freq-card--{'{handle}'}
              </code>
            </p>
          </div>
        </div>
      </div>
    </div>;
}